import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { EntitiesTableList, useDisableUserModal } from '../../../components';
import { fetchUsers, clearUsersInfo, changeUserStatus, deleteUserRole, setUserStatusLocal } from '../../../store/users';
import { resendInvitationForUser, rescindInvitationForUser, sendNewPassword } from '../../../store/authorization';
import { useCurrentRole } from '../../../hooks/useCurrentRole';
import { useSearch } from '../../../hooks/useSearch';
import { renderColumns, transformData } from './renderColumns';
import { usePagination } from '../../../hooks/usePagination';
import { redirectToPage, renderRecordValue } from '../../../utils/helpers';
import { Navigation } from '../../../utils/navigation';
import { RESCIND_INVITE_MODAL_DESCRIPTION, USER_STATUSES } from '../../../constants/constants';

const Users = ({
  title,
  addNewBtnTitle,
  managedRole,
  users,
  myEmail,
  limit,
  currentPage,
  total,
  totalPages,
  fetchUsers,
  sendNewPassword,
  changeUserStatus,
  deleteUserRole,
  setUserStatusLocal,
  role,
  clearUsersInfo,
  resendInvitationForUser,
  rescindInvitationForUser
}) => {
  const navigate = Navigation();
  usePagination(navigate);

  useEffect(() => {
    fetchUsers({ userStatus: USER_STATUSES.ACTIVE, Navigation: navigate });

    return;
  }, [fetchUsers]);

  useEffect(() => () => clearUsersInfo(), [clearUsersInfo]);

  const [deleteUser, setDeleteUser] = useState(null);
  const [rescindUser, setRescindUser] = useState(null);
  const [selectedUserStatus, setSelectedUserStatus] = useState(USER_STATUSES.ACTIVE);

  const { searchValue, highlighted, handleChangeSearch } = useSearch(
    fetchUsers,
    () => ({ userStatus: selectedUserStatus })
  );
  const { isAdmin, isHOAdmin, isTCAdmin, isEnterpriseAdmin, isMHAdmin } = useCurrentRole(role);

  const handleConfirmDeleteUser = () => {
    deleteUserRole(deleteUser).then(() =>
      fetchUsers({ value: searchValue, userStatus: selectedUserStatus, Navigation: navigate })
    );
    setDisableUserModalVisible(false);
  };

  const { DisableUserModal, setDisableUserModalVisible } = useDisableUserModal({
    handleConfirm: handleConfirmDeleteUser,
    userName: renderRecordValue(deleteUser?.name),
    isDelete: true,
  });

  const handleConfirmRescindInvitation = () => {
    rescindInvitationForUser(rescindUser?.id).then(() => {
      setUserStatusLocal({ id: rescindUser?.id, status: USER_STATUSES.EXPIRED });
      setDisableRescindModalVisible(false);
      setRescindUser(null);
    });
  };

  const { DisableUserModal: RescindInvitationModal, setDisableUserModalVisible: setDisableRescindModalVisible } = useDisableUserModal({
    handleConfirm: handleConfirmRescindInvitation,
    userName: renderRecordValue(rescindUser?.name),
    modalDescription: RESCIND_INVITE_MODAL_DESCRIPTION.replace('{{userName}}', rescindUser?.name || 'this user'),
  });

  const handleManageUser = useCallback(
    (id, Navigation) => () => navigate.push(`${Navigation.location.pathname}/${id}/edit`),
    [navigate],
  );

  const handleSendPassword = useCallback((id) => () => sendNewPassword(id), [sendNewPassword]);

  const handleResendInvitation = useCallback(
    (id) => () => resendInvitationForUser(id).then(() => setUserStatusLocal({ id, status: USER_STATUSES.INVITED })),
    [resendInvitationForUser, setUserStatusLocal]
  );
  const handleRescindInvitation = useCallback((id, name) => () => {
    setRescindUser({ id, name });
    setDisableRescindModalVisible(true);
  }, []);

  const switchUserStatus = useCallback((payload) => () => changeUserStatus(payload), [changeUserStatus]);

  const archieveUserRole = useCallback((payload) => () => {
    setDeleteUser(payload);
    setDisableUserModalVisible(true)
  }, []);

  const handleChangePage = (page) => {
    redirectToPage(navigate?.location.pathname, page, navigate);
    fetchUsers({ value: searchValue, userStatus: selectedUserStatus, filtered: null, Navigation: navigate });
  };

  const handleUserStatus = (value) => {
    setSelectedUserStatus(value);
    redirectToPage(navigate?.location.pathname, 1, navigate);
    fetchUsers({ value: searchValue, userStatus: value, filtered: true, Navigation: navigate });
  };

  const tableData = useMemo(() => {
    const filteredUsers = (() => {
      if (selectedUserStatus === 'ALL') return users;
      if (selectedUserStatus === USER_STATUSES.ACTIVE) {
        return users?.filter((u) => [USER_STATUSES.ACTIVE, USER_STATUSES.INVITED].includes(u.status));
      }
      if (selectedUserStatus === USER_STATUSES.DISABLED) {
        return users?.filter((u) => [USER_STATUSES.DISABLED, USER_STATUSES.EXPIRED].includes(u.status));
      }
      return users?.filter((u) => u.status === selectedUserStatus);
    })();

    const data = transformData({
      data: filteredUsers,
      switchUserStatus,
      archieveUserRole,
      handleManageUser,
      myEmail,
      managedRole,
      isAdmin,
      isMHAdmin,
      isHOAdmin,
      isEnterpriseAdmin,
      handleSendPassword,
      handleResendInvitation,
      handleRescindInvitation,
      Navigation: navigate
    });


    return data;
  }, [users, selectedUserStatus, handleManageUser, isAdmin, isMHAdmin, isHOAdmin, isEnterpriseAdmin, myEmail, handleSendPassword, managedRole, switchUserStatus, archieveUserRole, handleResendInvitation, handleRescindInvitation, navigate]);

  const columns = useMemo(() => {
    return role && users?.length > 0 ? renderColumns(isAdmin, isMHAdmin, isHOAdmin, searchValue, highlighted) : null;
  }, [role, users, isAdmin, highlighted, searchValue]);

  return (
    <>
      <EntitiesTableList
        title={title}
        addNewBtnTitle={addNewBtnTitle}
        Navigation={navigate}
        columns={columns}
        data={tableData}
        handleChangePage={handleChangePage}
        showAddNewButton={isAdmin || isEnterpriseAdmin}
        showAddNewAdminButton={isHOAdmin || isTCAdmin}
        handleChangeSearch={handleChangeSearch}
        searchValue={searchValue}
        limit={limit}
        currentPage={currentPage}
        total={total}
        totalPages={totalPages}
        handleUserStatus={handleUserStatus}
        selectedUserStatus={selectedUserStatus}
        showUserStatus={isAdmin && !isEnterpriseAdmin}
      />

      {DisableUserModal}
      {RescindInvitationModal}
    </>
  );
};

const mapStateToProps = (state) => ({
  users: state.users.users,
  myEmail: state.authorization?.user?.email,
  limit: state.users.limit,
  currentPage: state.users.currentPage,
  total: state.users.total,
  totalPages: state.users.totalPages,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({
    fetchUsers,
    sendNewPassword,
    resendInvitationForUser,
    rescindInvitationForUser,
    clearUsersInfo,
    changeUserStatus,
    deleteUserRole,
    setUserStatusLocal
  }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(Users);
